import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import { SyutTreeGrid, type TreeGridColumn, MinimizedModalStrip, SyutSelect, SyutTreeSelect } from 'components/ui';
import type { TreeSelectOption } from 'components/ui/SyutTreeSelect/SyutTreeSelect';
import type { ActionButton, ViewModeOption } from 'components/modals/common/ModuleHeader';
import notificationService from '../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { moduleService } from 'api/services';
import type {
  MenuDto, CreateMenuRequest, UpdateMenuRequest,
  MenuScreenDto,
} from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect/SyutSelectUnified';

// ============================================================================
// MENU FORM MODAL
// ============================================================================

interface MenuFormModalProps {
  open: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: MenuDto | null;
  moduleId: string;
  moduleName: string;
  allMenus: MenuDto[];
  onClose: () => void;
  onSave: () => void;
  tabId?: string;
  screenModuleName?: string;
  instanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const MenuFormModal: React.FC<MenuFormModalProps> = ({
  open, mode, record, moduleId, moduleName: modName, allMenus, onClose, onSave,
  tabId, screenModuleName, instanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [formData, setFormData] = useState({ menuCode: '', menuName: '', parentMenuId: '', iconKey: '', routePath: '', sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const savedSnapshot = useRef<string | null>(null);

  // Refs for minimize/restore — captures current state in closure
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const isRestoreRef = useRef(false);

  // Linked screen state — selectedScreenId is local; saved only on form submit
  const [linkedScreen, setLinkedScreen] = useState<MenuScreenDto | null>(null);
  const [screensLoading, setScreensLoading] = useState(false);
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null);
  const selectedScreenIdRef = useRef<string | null>(null);
  selectedScreenIdRef.current = selectedScreenId;
  const originalScreenIdRef = useRef<string | null>(null);

  // A menu cannot link screens if it has child menus (only leaf menus can link screens)
  const cannotLinkScreen = useMemo(() => {
    if (mode === 'edit' && record && allMenus.some(m => String(m.parentMenuId) === String(record.id))) return true;
    return false;
  }, [allMenus, record, mode]);

  // Seed option for the currently linked screen (so SyutSelect can resolve its label)
  const linkedScreenSeedOption = useMemo<SelectOption[]>(() => {
    if (!linkedScreen) return [];
    return [{ value: String(linkedScreen.screenId), label: `${linkedScreen.screenCode} — ${linkedScreen.screenName}` }];
  }, [linkedScreen]);

  const loadLinkedScreen = useCallback(async (menuId: string | number) => {
    setScreensLoading(true);
    try {
      const res = await moduleService.getScreensByMenu(menuId);
      const screens = res.data || [];
      const screen = screens.length > 0 ? screens[0] : null;
      setLinkedScreen(screen);
      const screenId = screen ? String(screen.screenId) : null;
      setSelectedScreenId(screenId);
      originalScreenIdRef.current = screenId;
    } catch {
      notificationService.toast.error('Failed to load linked screen');
    } finally {
      setScreensLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Skip form reset when restoring from minimized — form data already restored in onRestore closure
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    if (mode === 'edit' && record) {
      const fd = { menuCode: record.menuCode, menuName: record.menuName, parentMenuId: record.parentMenuId ? String(record.parentMenuId) : '', iconKey: record.iconKey || '', routePath: record.routePath || '', sortOrder: record.sortOrder };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
      loadLinkedScreen(record.id);
    } else if (mode === 'copy' && record) {
      const fd = { menuCode: '', menuName: record.menuName + ' (Copy)', parentMenuId: record.parentMenuId ? String(record.parentMenuId) : '', iconKey: record.iconKey || '', routePath: record.routePath || '', sortOrder: record.sortOrder };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
      setLinkedScreen(null);
      setSelectedScreenId(null);
      originalScreenIdRef.current = null;
    } else {
      const fd = { menuCode: '', menuName: '', parentMenuId: '', iconKey: '', routePath: '', sortOrder: 0 };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
      setLinkedScreen(null);
      setSelectedScreenId(null);
      originalScreenIdRef.current = null;
    }
    setFieldErrors({});
  }, [open, mode, record, loadLinkedScreen]);

  // Parent menu tree options — SyutTreeSelect handles self+descendant exclusion via excludeValue
  const parentMenuTreeOptions: TreeSelectOption[] = useMemo(() => {
    return allMenus.map(m => ({
      value: String(m.id),
      label: `${m.menuCode} — ${m.menuName}`,
      parentValue: m.parentMenuId ? String(m.parentMenuId) : null,
      sortOrder: m.sortOrder,
    }));
  }, [allMenus]);

  // Screen search for linking
  const loadScreenOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await moduleService.getAllScreens({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 200,
    });
    return res.data.map(s => ({ value: String(s.id), label: `${s.screenCode} — ${s.screenName}` }));
  }, []);

  // Handle screen selection change — local state only, saved on form submit
  const handleScreenChange = useCallback((val: string | string[] | null) => {
    setSelectedScreenId(val ? String(val) : null);
  }, []);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentFormData = { ...formDataRef.current };
    const currentSnapshot = savedSnapshot.current || '';
    const currentScreenId = selectedScreenIdRef.current;
    const currentOriginalScreenId = originalScreenIdRef.current;
    const formDirty = currentSnapshot ? JSON.stringify(currentFormData) !== currentSnapshot : false;
    const screenDirty = currentScreenId !== currentOriginalScreenId;
    const currentIsDirty = formDirty || screenDirty;
    const idx = instanceId || '1';
    let title: string;
    if (mode === 'create' || mode === 'copy') {
      const prefix = mode === 'copy' ? 'Copy' : 'Add';
      title = currentFormData.menuCode ? `${prefix} - ${currentFormData.menuCode} (${idx})` : currentFormData.menuName ? `${prefix} - ${currentFormData.menuName} (${idx})` : `${prefix} New (${idx})`;
    } else {
      title = `Edit - ${record?.menuCode || currentFormData.menuCode}`;
    }
    minimize({
      id: tabId ? `${tabId}-menu-modal-${mode === 'edit' ? record?.id : mode + '-' + instanceId}` : 'menu-modal',
      tabId: tabId!,
      moduleName: screenModuleName || 'Menu Master',
      title,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(currentFormData);
        savedSnapshot.current = currentSnapshot;
        setSelectedScreenId(currentScreenId);
        originalScreenIdRef.current = currentOriginalScreenId;
        onRestoreModal?.(mode, instanceId, mode !== 'create' ? record?.menuCode : undefined);
      },
    });
    onClose();
  }, [tabId, instanceId, mode, record, minimize, screenModuleName, onRestoreModal, onClose]);

  useEffect(() => { if (minimizeRef) minimizeRef.current = handleMinimize; }, [minimizeRef, handleMinimize]);

  const handleClose = useCallback(() => {
    const formDirty = savedSnapshot.current ? JSON.stringify(formData) !== savedSnapshot.current : false;
    const screenDirty = selectedScreenId !== originalScreenIdRef.current;
    if (formDirty || screenDirty) {
      setCloseConfirmOpen(true);
    } else {
      onClose();
    }
  }, [formData, selectedScreenId, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.menuCode.trim()) errors.menuCode = 'Menu Code is required';
    if (!formData.menuName.trim()) errors.menuName = 'Menu Name is required';
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      if (mode === 'edit' && record) {
        const req: UpdateMenuRequest = { moduleId, menuCode: formData.menuCode.toUpperCase(), menuName: formData.menuName, parentMenuId: formData.parentMenuId || undefined, iconKey: formData.iconKey || undefined, routePath: formData.routePath || undefined, sortOrder: formData.sortOrder, isActive: true, rowVersion: record.rowVersion, screenId: selectedScreenId || undefined };
        await moduleService.updateMenu(moduleId, record.id, req);
        notificationService.toast.success(`Menu "${formData.menuName}" has been updated successfully.`);
      } else {
        const req: CreateMenuRequest = { moduleId, menuCode: formData.menuCode.toUpperCase(), menuName: formData.menuName, parentMenuId: formData.parentMenuId || undefined, iconKey: formData.iconKey || undefined, routePath: formData.routePath || undefined, sortOrder: formData.sortOrder, isActive: true, screenId: selectedScreenId || undefined };
        await moduleService.createMenu(moduleId, req);
        notificationService.toast.success(`Menu "${formData.menuName}" has been created successfully.`);
      }
      onSave();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [formData, mode, record, moduleId, selectedScreenId, onSave]);

  if (!open) return null;
  const title = mode === 'create' ? 'Add Menu' : mode === 'copy' ? 'Copy Menu' : `Edit Menu — ${record?.menuCode}`;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}><IconSystem.chevronDown size={16} /></button>}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>
        <div className="modal-content">
          {/* Module (read-only context) */}
          <div className="form-group" style={{ marginBottom: '0.75rem' }}>
            <label className="form-label">Module</label>
            <input type="text" className="form-input" value={modName} readOnly style={{ background: 'var(--bg-secondary)' }} />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Menu Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.menuCode ? ' form-field-error' : ''}`}
                value={formData.menuCode}
                onChange={e => { setFormData(p => ({ ...p, menuCode: e.target.value.toUpperCase() })); setFieldErrors(prev => { const { menuCode, ...rest } = prev; return rest; }); }}
                placeholder="e.g., MNU_IAM"
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={50}
              />
              {fieldErrors.menuCode && <div className="form-error-message">{fieldErrors.menuCode}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Menu Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.menuName ? ' form-field-error' : ''}`}
                value={formData.menuName}
                onChange={e => { setFormData(p => ({ ...p, menuName: e.target.value })); setFieldErrors(prev => { const { menuName, ...rest } = prev; return rest; }); }}
                placeholder="e.g., IAM Settings"
                maxLength={150}
              />
              {fieldErrors.menuName && <div className="form-error-message">{fieldErrors.menuName}</div>}
            </div>
          </div>
          <div className="form-grid-2">
            <SyutTreeSelect
              label="Parent Menu"
              options={parentMenuTreeOptions}
              value={formData.parentMenuId || null}
              onChange={val => setFormData(p => ({ ...p, parentMenuId: val as string || '' }))}
              isClearable
              menuPortalTarget={document.body}
              excludeValue={mode === 'edit' && record ? String(record.id) : undefined}
              showChildCount
            />
            <div className="form-group">
              <label className="form-label">Icon Key</label>
              <input type="text" className="form-input" value={formData.iconKey} onChange={e => setFormData(p => ({ ...p, iconKey: e.target.value }))} maxLength={100} placeholder="e.g. shield, users" />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Route Path</label>
              <input type="text" className="form-input" value={formData.routePath} onChange={e => setFormData(p => ({ ...p, routePath: e.target.value }))} maxLength={200} placeholder="e.g. /iam/roles" />
            </div>
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <input type="number" className="form-input" value={formData.sortOrder} onChange={e => setFormData(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>

          {/* Linked Screen (one screen per menu) */}
          <div style={{ marginTop: '0.75rem' }}>
            {cannotLinkScreen ? (
              <SyutSelect
                label="Linked Screen"
                options={[]}
                value=""
                onChange={() => {}}
                isDisabled
                placeholder="This menu has child items — only leaf menus can link screens"
                menuPortalTarget={document.body}
              />
            ) : screensLoading ? (
              <SyutSelect label="Linked Screen" options={[]} value="" onChange={() => {}} isDisabled placeholder="Loading..." menuPortalTarget={document.body} />
            ) : (
              <SyutSelect
                label="Linked Screen"
                isAsync
                loadOptions={loadScreenOptions}
                defaultOptions
                cacheOptions={false}
                value={selectedScreenId || ''}
                seedOptions={linkedScreenSeedOption}
                onChange={handleScreenChange}
                placeholder="Select a screen to link..."
                isClearable
                menuPortalTarget={document.body}
              />
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create Menu'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={mode === 'edit' ? `Save changes to menu "${formData.menuName}"?` : `Create menu "${formData.menuName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close? Your changes will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={() => { setCloseConfirmOpen(false); onClose(); }}
        onCancel={() => setCloseConfirmOpen(false)}
        variant="danger"
      />
    </>
  );
};


// ============================================================================
// MENU MASTER SCREEN
// ============================================================================

interface MenuMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

type GridRecord = Omit<MenuDto, 'id'> & { id: string };

const MenuMasterScreen: React.FC<MenuMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized } = useModalMinimize();

  // View & filter state
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [filterOpen, setFilterOpen] = useState(false);

  // Module filter
  const [selectedModuleId, setSelectedModuleId] = useState<string>('');
  const [selectedModuleName, setSelectedModuleName] = useState<string>('');

  // Grid state
  const [allMenus, setAllMenus] = useState<MenuDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<MenuDto | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Load modules for the filter dropdown
  const loadModuleOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await moduleService.getAllModules({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 500,
    });
    return res.data.map(m => ({ value: String(m.id), label: `${m.moduleCode} — ${m.moduleName}` }));
  }, []);

  // Load ALL menus for the module — used for tree grid + tree select
  // showLoading=false for silent refresh after save (avoids blank grid flash)
  const loadAllMenus = useCallback(async (modId: string, showLoading = true) => {
    if (!modId) { setAllMenus([]); return; }
    if (showLoading) setLoading(true);
    try {
      const res = await moduleService.getMenusByModule(modId);
      setAllMenus(res.data || []);
    } catch {
      setAllMenus([]);
      notificationService.toast.error('Failed to load menus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAllMenus(selectedModuleId); }, [selectedModuleId, loadAllMenus]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Tree-aware search filter: matching nodes + all ancestors preserved
  const filteredMenus = useMemo(() => {
    if (!debouncedSearch.trim()) return allMenus;
    const q = debouncedSearch.toLowerCase().trim();
    const matchingIds = new Set<string>();
    for (const m of allMenus) {
      if (m.menuCode.toLowerCase().includes(q) || m.menuName.toLowerCase().includes(q)) {
        matchingIds.add(String(m.id));
      }
    }
    if (matchingIds.size === 0) return [];
    const menuById = new Map(allMenus.map(m => [String(m.id), m]));
    const includedIds = new Set<string>(matchingIds);
    for (const id of matchingIds) {
      let current = menuById.get(id);
      while (current?.parentMenuId) {
        const parentId = String(current.parentMenuId);
        if (includedIds.has(parentId)) break;
        includedIds.add(parentId);
        current = menuById.get(parentId);
      }
    }
    return allMenus.filter(m => includedIds.has(String(m.id)));
  }, [allMenus, debouncedSearch]);

  const refetch = useCallback(() => loadAllMenus(selectedModuleId), [loadAllMenus, selectedModuleId]);

  const handleSaveSuccess = useCallback(() => { setFormModalOpen(false); setSelectedRecord(null); loadAllMenus(selectedModuleId, false); }, [loadAllMenus, selectedModuleId]);

  // Track module options for label resolution
  const moduleOptionsRef = useRef<SelectOption[]>([]);
  const wrappedLoadModuleOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const opts = await loadModuleOptions(inputValue);
    if (!inputValue) moduleOptionsRef.current = opts; // cache default list
    return opts;
  }, [loadModuleOptions]);

  // Module selection handler
  const handleModuleChange = useCallback((val: string | string[] | null) => {
    const modId = val ? String(val) : '';
    setSelectedModuleId(modId);
    if (modId) {
      const opt = moduleOptionsRef.current.find(o => o.value === modId);
      if (opt?.label) {
        const parts = opt.label.split(' — ');
        setSelectedModuleName(parts.length > 1 ? parts[1] : opt.label);
      }
    } else {
      setSelectedModuleName('');
    }
  }, []);

  // CRUD handlers
  const handleAdd = useCallback(() => {
    if (!selectedModuleId) { notificationService.toast.warning('Please select a module first.'); return; }
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    createInstanceCounter.current += 1;
    setCreateInstanceId(String(createInstanceCounter.current));
    setSelectedRecord(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen, selectedModuleId]);

  const handleEdit = useCallback((menu: MenuDto) => {
    const modalId = `${tabId}-menu-modal-${menu.id}`;
    if (tabId && isMinimized(modalId)) { dismissMinimized(modalId); setSelectedRecord(menu); setFormMode('edit'); setFormModalOpen(true); return; }
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    setSelectedRecord(menu);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [formModalOpen, tabId, isMinimized, dismissMinimized]);

  const handleCopy = useCallback((menu: MenuDto) => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    copyInstanceCounter.current += 1;
    setCopyInstanceId(String(copyInstanceCounter.current));
    setSelectedRecord(menu);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleDeleteRequest = useCallback((menu: MenuDto) => { setSelectedRecord(menu); setDeleteModalOpen(true); }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedRecord || !selectedModuleId) return;
    setIsDeleting(true);
    try {
      await moduleService.deleteMenu(selectedModuleId, selectedRecord.id);
      notificationService.toast.success(`Menu "${selectedRecord.menuName}" has been deleted successfully.`);
      setDeleteModalOpen(false);
      setSelectedRecord(null);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to delete menu');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRecord, selectedModuleId, refetch]);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instId?: string, code?: string) => {
    if (instId) { if (mode === 'copy') setCopyInstanceId(instId); else setCreateInstanceId(instId); }
    if (code) { const rec = allMenus.find(r => r.menuCode === code); if (rec) setSelectedRecord(rec); }
    setFormMode(mode);
    setFormModalOpen(true);
  }, [allMenus]);

  // Tree columns
  const columns: TreeGridColumn<GridRecord>[] = useMemo(() => [
    { id: 'menuName', header: 'Menu Name', accessorKey: 'menuName', size: 300, isTreeColumn: true },
    { id: 'menuCode', header: 'Code', accessorKey: 'menuCode', size: 130, cell: ({ row }: any) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.original.menuCode}</span> },
    { id: 'routePath', header: 'Route', accessorKey: 'routePath', size: 180, cell: ({ row }: any) => <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{row.original.routePath || '—'}</span> },
    { id: 'iconKey', header: 'Icon', accessorKey: 'iconKey', size: 100, cell: ({ row }: any) => <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{row.original.iconKey || '—'}</span> },
    { id: 'sortOrder', header: 'Order', accessorKey: 'sortOrder', size: 70, cell: ({ row }: any) => <span style={{ textAlign: 'center', display: 'block' }}>{row.original.sortOrder}</span> },
  ], []);

  // Row actions for tree grid
  const rowActionsForMenu = useCallback((row: GridRecord) => {
    const m = row as unknown as MenuDto;
    return [
      { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => handleEdit(m) },
      { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={14} />, onClick: () => handleCopy(m) },
      { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={14} />, onClick: () => handleDeleteRequest(m), variant: 'danger' as const },
    ];
  }, [handleEdit, handleCopy, handleDeleteRequest]);

  const treeData = filteredMenus as unknown as GridRecord[];

  // View mode options
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View (Not Applicable)', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // Action buttons
  const actionButtons: ActionButton[] = [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Menu', onClick: handleAdd, tooltip: selectedModuleId ? 'Add New Menu' : 'Select a module first', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => {}, tooltip: 'Export', variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Menu Master"
        description="Manage menus and link screens within modules"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search menus...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as 'list' | 'grid') }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '3px' }}>
        <div className="data-grid-container">
          {/* Module Selector Bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '4px 1rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '6px',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Module:</span>
            <div className="module-ribbon-selector" style={{ flex: '1 1 400px', maxWidth: '500px', minWidth: '200px' }}>
              <style dangerouslySetInnerHTML={{ __html: `
                .module-ribbon-selector .syut-select__control {
                  outline: 1px solid rgba(148, 163, 184, 0.35) !important;
                  outline-offset: -1px !important;
                  border: none !important;
                  box-shadow: none !important;
                  background: var(--bg-primary, #ffffff) !important;
                }
              `}} />
              <SyutSelect
                isAsync
                loadOptions={wrappedLoadModuleOptions}
                defaultOptions
                cacheOptions={false}
                value={selectedModuleId}
                onChange={handleModuleChange}
                placeholder="Select a module to manage menus..."
                isClearable
                size="sm"
              />
            </div>
            {selectedModuleId && allMenus.length > 0 && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {allMenus.length} menu{allMenus.length !== 1 ? 's' : ''} total
                {debouncedSearch.trim() && filteredMenus.length < allMenus.length && (
                  <> &middot; {filteredMenus.length} matching</>
                )}
              </span>
            )}
          </div>

          {!selectedModuleId ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <IconSystem.menu size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Select a Module</h3>
              <p>Choose a module from the dropdown above to view and manage its menus.</p>
            </div>
          ) : (
            <SyutTreeGrid
              data={treeData}
              columns={columns}
              parentKey="parentMenuId"
              sortKey="sortOrder"
              defaultExpandLevel={debouncedSearch.trim() ? -1 : 0}
              rowActions={rowActionsForMenu}
              isLoading={loading}
              emptyMessage="No menus found for this module"
            />
          )}
        </div>
      </div>

      {selectedModuleId && (
        <MenuFormModal
          open={formModalOpen}
          mode={formMode}
          record={selectedRecord}
          moduleId={selectedModuleId}
          moduleName={selectedModuleName}
          allMenus={allMenus}
          onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
          onSave={handleSaveSuccess}
          tabId={tabId}
          screenModuleName={moduleName}
          instanceId={formMode === 'copy' ? copyInstanceId : createInstanceId}
          minimizeRef={formModalMinimizeRef}
          onRestoreModal={handleRestoreModal}
        />
      )}

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Menu"
        message={`Delete menu "${selectedRecord?.menuName}"? This cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteModalOpen(false); setSelectedRecord(null); }}
        variant="danger"
      />
    </div>
  );
};

export default MenuMasterScreen;
